// @ts-check
// ── imports explicites (Phase 4B, générés puis maintenus à la main) ──
import { useEffect, useState } from "./00-boot.jsx";
import { OTHER_MAP, countryOfItem, distanceKm, findCity, itemPoint, langPick, norm } from "./01-taxonomy-cities.jsx";
import { LUX_TZ, _displayEpoch, localeForLang, pad2, pickupToEpoch, wallNowInTZ } from "./02-chat-translate-time.jsx";
import { ACTIVE_LANG, tx } from "./04-i18n-dict.jsx";
// ─── Modèle « retrait seul » (2026-07-05) ────────────────────────────────────
// La date de retrait est l'UNIQUE réglage utilisateur ; la durée (DEFAULT_RESERVATION_HOURS)
// reste un plancher interne du calcul d'expiresAt (formule Lot D1 inchangée). Les affichages
// dérivent 3 états de pickupAt/expiresAt existants — aucune donnée nouvelle :
//   before (avant le retrait) · grace (fenêtre pickup→expiresAt : « Retrait en cours — confirmez »)
//   · over (échue, gérée par les chemins d'expiration existants).
const reservationLiveState=(pickupAt,expiresAt,now=Date.now(),tz=LUX_TZ)=>{
  const pk=pickupToEpoch(pickupAt,tz);
  const ex=Number(expiresAt||0);
  if(ex&&now>=ex) return "over";
  if(Number.isFinite(pk)&&now>=pk) return "grace";
  return "before";
};
// ⭐⭐ C7 · T30, lot B (21/09/2026) — LE CYCLE D'UNE OFFRE SE LIT DANS LE FIL : IL NE SE DEVINE PAS.
//  T29 faisait dire au pied de la carte d'offre où en était son échange, mais il le DEVINAIT : la dernière issue du fil
//  entier, reconnue à l'heure (46 rappels d'avis et 42 confirmations de VISITE lus comme des accords), l'heure de retrait
//  lue dans une COPIE écrite une fois, l'échéance mesurée à la minute. Le fil porte pourtant des LIENS, écrits ou validés
//  par la base, et c'est eux qu'on lit :
//   · l'ANCRE : la dernière demande de réservation née de cette offre (`fromAcceptedOfferId`, que la base retire s'il
//     ne désigne pas une vraie offre acceptée du même auteur dans ce fil) ;
//   · le CYCLE : l'ancre et les reports qui la suivent, jusqu'à la demande suivante. Un report ne porte pas le lien : il
//     continue la réservation en cours (la base lui retire sa marque s'il n'y a pas de réservation acceptée) ;
//   · ce qui l'a CONCLU : une carte d'incident née d'une carte du cycle (`sourceMessageId`), une confirmation d'accord
//     de TRANSACTION (`i18nKey` ; jamais une visite, jamais un rappel d'avis), une « transaction à confirmer » confirmée.
//  Mesuré le 21/09 sur les 33 fils concernés (140 cartes, tests/fixtures/t30-cycles-reels.json) :
//   · 11 ancres de juin portent AUSSI la marque de report (un client d'alors reportait sur la carte même) : l'ancre est
//     l'ancre, quelle que soit sa marque ;
//   · le tampon de fin se pose sur la carte qu'on a touchée, l'ancre OU un report accepté (a686dd86 : la vente est sur
//     le report, l'ancre est restée « acceptée ») : on lit le dernier tampon des cartes qui TIENNENT la réservation ;
//   · 3 offres ont été réservées deux fois : la dernière ancre fait foi ;
//   · un fil peut se contredire (818fc3a5 : une transaction confirmée le 22/06, puis un signalement clos sans réponse
//     le 11/07) : l'issue retenue est la DERNIÈRE dans l'ordre du temps, celle que le bas du fil dit aussi.
//     Un « problème résolu » (`resolved`) clôt SANS transaction, comme le dit la carte d'incident (T29 le lisait comme un
//     accord). ⚠️ On ne DATE que ce dont on connaît l'instant : la confirmation d'accord (postée au moment où elle a
//     lieu) et la clôture d'un dossier (`closedAt`, écrit par la base, T30 lot A). Une carte d'incident NÉE close
//     (autoResolved sans instant de clôture : écrite sans dossier ouvert) n'a rien clos, elle ne compte pas.
//  Deux fonctions PURES : `offerCycleOf` (ce que le fil SAIT, sans horloge) et `offerFooterPhase` (ce qu'on en dit À CET
//  INSTANT : une réservation confirmée garde ses deux heures de grâce, `reservationLiveState`, comme partout ailleurs ;
//  un créneau encore sur la table se juge à la minute, comme le bouton qui l'accepterait, `acceptSlotValid`).
//  Filet : tests/une-carte-lit-son-cycle.test.mjs (les 33 fils réels, exécutés).
//  ⭐ Lot C (22/09) — UNE ANNULATION DÉCLARÉE PAR LE VENDEUR LUI-MÊME N'EST PAS UN SIGNALEMENT. Le vendeur qui annule
//   sa propre réservation (ou la vente en cours, sur « transaction à confirmer ») pose son tampon avec `selfDeclared` :
//   aucun dossier ne s'ouvre, rien ne « se règle » ensuite (la frise le dit « Sans suite » depuis le 13/07). Le modèle
//   le lisait « signalé », pour toujours : c'est une ANNULATION, par le vendeur.
const OFFER_CYCLE_HELD=["accepted","counterAccepted"];
//  (lot C · R21, 28/10/2026) les raisons d'une carte close par la base qui GARDE son statut accepté (ni tampon, ni statut neuf)
const OFFER_CYCLE_BASE_CLOSED=["compromise_released"];
const OFFER_CYCLE_DEAL_KEYS=["flow.transactionConfirmedReview","flow.transactionAutoConfirmed"];
const OFFER_CYCLE_WAITING=["pending","resolve_requested","final_pending"];
const OFFER_CYCLE_CLOSED=["accepted","disputed","resolved","resolved_final_done","resolved_final_not_done"];
const offerCycleOf=(messages,offer)=>{
  const id=offer&&offer.id!=null?String(offer.id):"";
  if(!id) return null;
  const donnees=m=>(m&&m.data&&typeof m.data==="object")?m.data:{};
  const fini=x=>{const n=Number(x);return Number.isFinite(n)&&n>0?n:null;};
  const tampon=m=>m.reservationAction||donnees(m).reservationAction||null;
  const fil=(Array.isArray(messages)?messages:[]).filter(m=>m&&typeof m==="object")
    .map((m,i)=>({m,i,t:Number(m.ts)||0})).sort((a,b)=>(a.t-b.t)||(a.i-b.i)).map(x=>x.m);
  let a=-1;
  fil.forEach((m,i)=>{if(m.kind==="reserve"&&String(donnees(m).fromAcceptedOfferId||"")===id) a=i;});
  if(a<0){
    // L'ancre n'est pas dans ce qu'on a chargé : on dit ce que dit l'offre elle-même (le reçu d'avant), rien de plus.
    const d=donnees(offer);
    return d.reservationStartedAt?{state:"request",reason:"copy",at:null,pickupAt:d.reservationPickupAt||null,expiresAt:null,anchorId:null}:null;
  }
  const ancre=fil[a],cartes=[ancre],debut=Number(ancre.ts)||0;
  let fin=Infinity;
  for(let i=a+1;i<fil.length;i++){
    const m=fil[i];
    if(m.kind!=="reserve") continue;
    if(donnees(m).extensionRequest&&!donnees(m).fromAcceptedOfferId){cartes.push(m);continue;}
    fin=Number(m.ts)||0;break;
  }
  const dansLeCycle=m=>{const t=Number(m.ts)||0;return t>=debut&&t<fin;};
  const sources=new Set(cartes.concat(fil.filter(m=>m.kind==="dealConfirm"&&dansLeCycle(m))).map(m=>String(m.id)));
  let issue=null;
  const retenir=(as,ordre,at)=>{if(!issue||ordre>=issue.ordre) issue={as,ordre,at};};
  fil.forEach(m=>{
    const d=donnees(m),t=Number(m.ts)||0;
    if(m.kind==="dealConfirmed"&&dansLeCycle(m)&&OFFER_CYCLE_DEAL_KEYS.includes(d.i18nKey)) retenir("deal",t,t||null);
    else if(m.kind==="dealConfirm"&&dansLeCycle(m)&&m.status==="confirmed") retenir("deal",t,null);
    else if(m.kind==="dealConfirm"&&dansLeCycle(m)&&tampon(m)==="seller_cancel"&&d.selfDeclared) retenir("seller_cancel",t,null);
    else if(m.kind==="incidentResponse"&&sources.has(String(d.sourceMessageId||""))){
      const st=m.status||"pending",ca=fini(d.closedAt);
      if(OFFER_CYCLE_WAITING.includes(st)) retenir("open",t,null);
      else if(OFFER_CYCLE_CLOSED.includes(st)&&!(d.autoResolved&&!ca))
        retenir(st==="resolved_final_done"?"deal":(st==="accepted"&&d.autoResolved)?"unanswered":"closed",ca||t,ca);
    }
  });
  const base={at:null,pickupAt:null,expiresAt:null,anchorId:String(ancre.id)};
  if(issue&&issue.as==="seller_cancel") return {...base,state:"cancelled",reason:"seller"};
  if(issue&&issue.as!=="open") return {...base,state:"closed",reason:issue.as,at:issue.at};
  //  (lot C · R21, 28/10/2026) une réservation que la BASE a close en gardant son statut (le compromis levé, sans suite après 30 jours) n'est plus tenue :
  //   sans cette exclusion, elle se disait « confirmée » (aucun tampon) — elle se dit close, avec sa raison, plus bas
  const tenues=cartes.filter(m=>OFFER_CYCLE_HELD.includes(m.status)&&!OFFER_CYCLE_BASE_CLOSED.includes(donnees(m).autoClosedReason));
  const tamponTenu=tenues.map(tampon).filter(Boolean).pop()||null;
  if(issue) return {...base,state:"reported",reason:tamponTenu};
  if(tenues.length){
    if(tamponTenu==="pickup"||tamponTenu==="sold") return {...base,state:"declared",reason:tamponTenu};
    if(tamponTenu==="cancel") return {...base,state:"cancelled",reason:"reservation"};
    //  (lot C · R15b, 27/10/2026) close par la base quand l'administration n'a pas approuvé l'annonce : ni un aveu du vendeur, ni une annulation signalée —
    //   « Réservation annulée. », les mots de la carte et de l'avis (relecture neuve : pas « demande close » pour une réservation acceptée)
    if(tamponTenu==="seller_cancel"&&tenues.some(m=>tampon(m)==="seller_cancel"&&donnees(m).autoClosedReason==="listing_moderated")) return {...base,state:"cancelled",reason:"reservation"};
    if(tamponTenu==="seller_cancel"&&tenues.some(m=>tampon(m)==="seller_cancel"&&donnees(m).selfDeclared)) return {...base,state:"cancelled",reason:"seller"};
    if(tamponTenu) return {...base,state:"reported",reason:tamponTenu};
    const eff=donnees(tenues[tenues.length-1]);
    return {...base,state:"confirmed",reason:null,pickupAt:eff.pickupAt||null,expiresAt:fini(eff.expiresAt)};
  }
  const raisons=cartes.map(m=>donnees(m).autoClosedReason).filter(Boolean);
  const annulee=cartes.some(m=>tampon(m)==="cancel"||m.status==="cancelled");
  //  Tenue un jour : un report n'existe que sur une réservation acceptée, et « délai de retrait passé » ne s'écrit que sur elle.
  if(cartes.length>1||raisons.includes("pickup_elapsed")){
    if(annulee) return {...base,state:"cancelled",reason:"reservation"};
    return {...base,state:"ended",reason:raisons.includes("pickup_elapsed")?"pickup_elapsed":raisons.includes("sold_elsewhere")?"sold_elsewhere":raisons.includes("listing_moderated")?"listing_moderated":raisons.includes("accepted_other")?"accepted_other":raisons.find(x=>OFFER_CYCLE_BASE_CLOSED.includes(x))||null};
  }
  const d=donnees(ancre),st=ancre.status||"pending",r=d.autoClosedReason||null;
  //  ⚠️ Retirée puis refusée faute de place (2e2820eb, un client d'avant le 03/09 laissait la demande « en attente ») :
  //   le tampon de l'auteur passe avant le statut que la base a posé ensuite — comme sur la carte de réservation.
  if(annulee) return {...base,state:"cancelled",reason:"request"};
  if(OFFER_CYCLE_BASE_CLOSED.includes(r)) return {...base,state:"ended",reason:r};   // (lot C · R21) close par la base, statut gardé : sa raison
  if(st==="pending") return {...base,state:"request",reason:null,pickupAt:d.buyerProposedPickupAt||d.proposedPickupAt||d.pickupAt||null};
  if(st==="countered") return {...base,state:"negotiation",reason:"seller",pickupAt:d.proposedPickupAt||d.pickupAt||null};
  if(st==="counterDeclined") return {...base,state:"negotiation",reason:"buyer",pickupAt:d.buyerProposedPickupAt||d.proposedPickupAt||d.pickupAt||null};
  //  24/09/2026 (vendu ailleurs) : une demande que la vente à quelqu'un d'autre a close n'a pas été « refusée » — elle se dit avec le
  //   mot de la notification que la base envoie (« Demande de réservation close »).
  if(st==="declined") return {...base,state:"ended",reason:r==="accepted_other"?"accepted_other":r==="sold_elsewhere"?"sold_elsewhere":r==="listing_moderated"?"listing_moderated":"declined"};
  return {...base,state:"ended",reason:["no_seller_response","no_buyer_response"].includes(r)?r:"expired"};
};
// ⭐⭐ 27/09/2026 — UNE RÉSERVATION ACCEPTÉE EST UNE CHAÎNE (C11 (6 quindecies)).
//  Un report accepté ne remplace pas la carte de la réservation : il s'AJOUTE. Le fil porte alors DEUX cartes acceptées —
//  l'origine (l'ANCIENNE heure) et le report (la nouvelle) —, et une fin (vente, retrait confirmé, annulation, « vendu à
//  quelqu'un d'autre ») n'en tamponne qu'UNE. Chacune proposait ses gestes : deux grilles, deux heures ; puis, la réservation
//  finie, la carte sœur gardait « Annuler / Confirmer / No-show » — sur une vente conclue (vu chez un membre réel, 19/07).
//  La chaîne : l'ORIGINE (la dernière carte de réservation de cet acheteur dans le fil jusqu'à la carte lue, hors report) et
//  les reports acceptés qui la suivent, jusqu'à la réservation suivante. Elle est CLOSE dès qu'UNE de ses cartes l'est
//  (tampon, clôture par la base, expiration) ; sa carte VIVANTE est la dernière acceptée — celle qui porte le retrait en cours.
//  ⚠️ MÊME RÈGLE QUE LA BASE (report_incident, migration « un incident se déclare sur un échange vivant ») : mêmes statuts,
//   même origine, mêmes marques de clôture — le banc reservation-chaine confronte les deux textes. Rend null si la carte n'est
//   pas une réservation tenue (en attente, refusée…) ou si le fil ne la contient pas : l'appelant garde alors son chemin d'hier.
const RESERVE_CHAIN_STATUSES=["accepted","counterAccepted","expired"];
const reservationChainOf=(messages,card)=>{
  if(!card||card.kind!=="reserve") return null;
  const donnees=m=>(m&&m.data&&typeof m.data==="object")?m.data:{};
  const tampon=m=>m.reservationAction||donnees(m).reservationAction||null;
  const acheteur=m=>String(donnees(m).buyerId||m.from||"");
  const qui=acheteur(card);
  const chaine=(Array.isArray(messages)?messages:[]).filter(m=>m&&typeof m==="object")
    .map((m,i)=>({m,i,t:Number(m.ts)||0})).sort((a,b)=>(a.t-b.t)||(a.i-b.i)).map(x=>x.m)
    .filter(m=>m.kind==="reserve"&&RESERVE_CHAIN_STATUSES.includes(m.status||"")&&acheteur(m)===qui);
  const pos=chaine.findIndex(m=>m===card||(card.id!=null&&String(m.id)===String(card.id)));
  if(pos<0) return null;
  const estOrigine=m=>!donnees(m).extensionRequest;
  let o=pos;
  while(o>=0&&!estOrigine(chaine[o])) o--;
  if(o<0) return null;
  let fin=chaine.length;
  for(let k=o+1;k<chaine.length;k++){ if(estOrigine(chaine[k])){ fin=k; break; } }
  const membres=chaine.slice(o,fin);
  const vivantes=membres.filter(m=>m.status==="accepted"||m.status==="counterAccepted");
  return {
    origine:membres[0],
    membres,
    vivante:vivantes.length?vivantes[vivantes.length-1]:null,
    close:membres.find(m=>!!tampon(m)||!!donnees(m).autoClosedReason||m.status==="expired")||null,
    derniere:fin===chaine.length,
  };
};
//  Ce que la carte d'une réservation TENUE peut encore dire, en une valeur simple (la mémo des lignes la compare) :
//   "live" — sa réservation vit et c'est elle qui la porte (ses gestes) ; "superseded:<retrait>" — un report accepté l'a
//   remplacée, la réservation continue sur la carte du report (dont le retrait est donné) ; "closed" — sa réservation est
//   finie (une autre carte de la chaîne l'a close, ou une réservation plus récente a suivi). null : ni réservation tenue, ni
//   chaîne lisible — la carte garde son chemin d'hier (ses propres marques).
const reservationChainState=(messages,card)=>{
  if(!card||card.kind!=="reserve"||!["accepted","counterAccepted"].includes(card.status||"")) return null;
  const c=reservationChainOf(messages,card);
  if(!c) return null;
  if(c.close||!c.derniere) return "closed";
  if(c.vivante&&c.vivante!==card&&!(card.id!=null&&String(c.vivante.id)===String(card.id))){
    const p=c.vivante.data&&typeof c.vivante.data==="object"?c.vivante.data.pickupAt:null;
    return "superseded:"+(p||"");
  }
  return "live";
};
// ⭐⭐ 28/09/2026 — UNE VISITE EST UNE PAIRE (C11 (6 quindecies), même famille que la chaîne d'une réservation).
//  La visite et sa carte de CLÔTURE (celle qui la cite, `sourceVisitId`) : une issue écrite sur l'une vaut pour l'autre.
//  L'écran estampille les deux d'un même geste, mais une écriture peut manquer ; et la base ferme la visite (réservée pour
//  un autre, vendue, « sans suite ») : la clôture gardait alors ses gestes (18 cartes réelles, juin–juillet).
//  "closed" — la visite n'est plus acceptée, la base l'a close ou elle porte un tampon, ou une AUTRE clôture de la même
//  visite a son issue ; "live" sinon ; null — pas une clôture en attente, ou sa visite n'est pas dans le fil (chemin
//  d'hier). La visite qui a EU LIEU (les deux marqueurs) reste lue par finalActionRdvSettled (useCardDecision), comme avant.
//  ⚠️ MÊME RÈGLE QUE LA BASE (report_incident, (d)), à une nuance près, voulue : la base laisse passer le tampon de CE
//   geste sans dossier (une application d'avant estampillait la paire AVANT de déclarer : son appel doit aboutir) ;
//   l'écran, lui, ne propose plus rien sur une paire tamponnée — il ne propose jamais ce que la base refuse.
const visitPairState=(messages,card)=>{
  if(!card||card.kind!=="finalAction"||(card.status||"pending")!=="pending") return null;
  const donnees=m=>(m&&m.data&&typeof m.data==="object")?m.data:{};
  const vid=donnees(card).sourceVisitId;
  if(vid==null||vid==="") return null;
  const fil=(Array.isArray(messages)?messages:[]).filter(m=>m&&typeof m==="object");
  const visite=fil.find(m=>m.kind==="visit"&&String(m.id)===String(vid));
  if(!visite) return null;
  const tampon=m=>m.reservationAction||donnees(m).reservationAction||null;
  if(!["accepted","counterAccepted"].includes(visite.status||"")||!!donnees(visite).autoClosedReason||!!tampon(visite)) return "closed";
  const soeurClose=fil.some(m=>m!==card&&!(card.id!=null&&String(m.id)===String(card.id))&&m.kind==="finalAction"
    &&String(donnees(m).sourceVisitId||"")===String(vid)
    &&((m.status||"pending")!=="pending"||!!donnees(m).autoClosedReason||!!tampon(m)));
  return soeurClose?"closed":"live";
};
//  Ce que la carte d'un ÉCHANGE TENU peut encore dire — une réservation acceptée (sa chaîne) ou la clôture d'une visite
//   (sa paire) — en une valeur simple : le fil, la feuille et le moteur de décision lisent CETTE fonction.
const exchangeStateOf=(messages,card)=>card&&card.kind==="finalAction"?visitPairState(messages,card):reservationChainState(messages,card);
const offerFooterPhase=(cycle,now=Date.now(),tz=LUX_TZ)=>{
  if(!cycle||!cycle.state) return null;
  if(cycle.state==="confirmed"){
    const v=reservationLiveState(cycle.pickupAt,cycle.expiresAt,now,tz);
    return v==="grace"?"pickup":v==="over"?"overdue":"confirmed";
  }
  if(cycle.state==="request"||cycle.state==="negotiation"){
    const t=cycle.pickupAt?pickupToEpoch(cycle.pickupAt,tz):NaN;
    return Number.isFinite(t)&&t<now-60000?"slotPast":cycle.state;
  }
  return cycle.state;
};
//  ⭐⭐ C7 · T30, lot C (22/09/2026) — CE QUE LE PIED DIT : UN ÉTAT À LA FOIS, AVEC LES MOTS DES AUTRES SURFACES.
//   Pure : une CLÉ de copie (`flow.offerCycle*`, dans les quatre langues), un TON — le jeton de couleur du flux que la
//   carte de réservation porte déjà pour le même état (`accent` en attente, `progress` contre-proposition, `ok`
//   réservation tenue, `neutral` sinon) — et les instants à dire : `date`, l'heure murale du retrait ; `day`, un instant
//   CONNU (confirmation d'accord, clôture d'un dossier). Aucun mot n'est neuf : chacun est celui que le dock, la
//   notification ou la carte de réservation dit déjà pour le même état (tests/tout-le-fil-dit-la-meme-chose.test.mjs
//   les compare) — on reprend le vocabulaire, on n'en crée pas.
//   `rearmed` : l'offre est acceptée et ne porte plus de réservation (la base l'a réarmée, 03/09) ; `listingOpen` :
//   l'annonce peut encore être réservée. Les deux ensemble, une nouvelle date peut être proposée : le pied ne dit plus
//   « aucune autre action », et dit au VENDEUR ce que l'acheteur peut faire (l'acheteur, lui, a le formulaire dessous).
//   Le retrait passé depuis plus de deux heures (`overdue`), avant la passe de la base (toutes les dix minutes), la
//   réservation TIENT encore : la carte de réservation garde ses gestes, le dock « Gérer la réservation ». Le pied dit
//   donc le FAIT — « réservation confirmée · le délai de retrait est passé » — et « expirée » seulement quand la base
//   l'a écrit (`ended:pickup_elapsed`).
//  (lot C · R15b, 27/10/2026) `listing_moderated` : « Demande de réservation close. » — le mot neutre de « vendu ailleurs », sans motif.
const OFFER_FOOTER_ENDED={pickup_elapsed:"offerCycleEndedPickup",accepted_other:"offerCycleEndedOther",sold_elsewhere:"offerCycleEndedSold",listing_moderated:"offerCycleEndedSold",compromise_released:"offerCycleEnded",declined:"offerCycleEndedDeclined",no_seller_response:"offerCycleEndedNoSeller",no_buyer_response:"offerCycleEndedNoBuyer",expired:"offerCycleEndedExpired"};
const offerFooterView=(cycle,phase,{reader="buyer",rearmed=false,listingOpen=true}={})=>{
  if(!cycle||!cycle.state||!phase) return null;
  const s=cycle.state,r=cycle.reason||null,relance=!!rearmed&&!!listingOpen;
  const vue=(key,tone,o={})=>({state:s,phase,reason:r,key,tone,date:null,day:null,tail:null,...o});
  const pourLeVendeur=relance&&reader==="seller"?"offerCycleRearmedSeller":null;
  const jour=Number(cycle.at)>0?Number(cycle.at):null;
  if(phase==="request") return vue(r==="copy"?"offerCycleCopy":"offerCycleRequest","accent",{date:cycle.pickupAt||null});
  if(phase==="negotiation") return vue("offerCycleNegotiation","progress",{date:cycle.pickupAt||null});
  if(phase==="slotPast") return vue(s==="negotiation"?"offerCycleNegotiationPast":"offerCycleRequestPast","neutral",{date:cycle.pickupAt||null});
  if(phase==="confirmed") return vue("offerCycleConfirmed","ok",{date:cycle.pickupAt||null});
  if(phase==="pickup") return vue("offerCyclePickup","accent");
  if(phase==="overdue") return vue("offerCycleOverdue","neutral");
  if(phase==="declared") return vue("offerCycleDeclared","accent");
  if(phase==="reported") return vue("offerCycleReported","neutral");
  if(s==="cancelled") return vue(r==="request"?"offerCycleCancelledRequest":r==="seller"?"offerCycleCancelledSeller":"offerCycleCancelledReservation","neutral",{tail:pourLeVendeur});
  if(s==="ended") return vue(OFFER_FOOTER_ENDED[r]||"offerCycleEnded","neutral",{tail:pourLeVendeur});
  if(s==="closed"){
    if(r==="deal") return vue(jour?"offerCycleDealOn":"offerCycleDeal","neutral",{day:jour});
    const cle=r==="unanswered"?(jour?"offerCycleUnansweredOn":"offerCycleUnanswered"):(jour?"offerCycleClosedOn":"offerCycleClosed");
    return vue(cle,"neutral",{day:jour,tail:relance?pourLeVendeur:"offerCycleNothingElse"});
  }
  return null;
};
// Date héro du retrait : « mer. 9 juil., 18:00 » (le rendez-vous, pas un timestamp technique).
const fmtPickupHero=(iso,lang=ACTIVE_LANG,tz=LUX_TZ)=>{
  const e=_displayEpoch(iso,tz);
  if(!Number.isFinite(e)) return "";
  return new Date(e).toLocaleString(localeForLang(lang),{weekday:"short",day:"numeric",month:"short",hour:"2-digit",minute:"2-digit",timeZone:tz});
};
// Pastille temporelle relative : « J−3 » (>48h) / « Demain » / « Aujourd'hui » — vide si passé.
//  Audit 23/09/2026 (lot client 2, constat #8) — LES JOURS SE COMPTENT AU CALENDRIER DE L'ANNONCE, pas en tranches de 24 h
//   depuis le minuit du TÉLÉPHONE : au passage à l'heure d'hiver (25/10/2026), un retrait le dimanche 23:30 vu le samedi
//   disait « J−2 » au lieu de « Demain » ; au printemps, un retrait demain 00:30 disait « Aujourd'hui ». La date MURALE de
//   l'instant, dans le fuseau de l'annonce, puis l'écart entre deux dates civiles (exact : Date.UTC n'a pas d'heure d'été).
const _wallDayUTC=(epoch,tz)=>{const m=/^(\d{4})-(\d{2})-(\d{2})/.exec(wallNowInTZ(tz,epoch-Date.now())||"");return m?Date.UTC(+m[1],+m[2]-1,+m[3]):NaN;};
const pickupDayChip=(pickupAt,lang=ACTIVE_LANG,now=Date.now(),tz=LUX_TZ)=>{
  const pk=pickupToEpoch(pickupAt,tz);
  if(!Number.isFinite(pk)||pk<=now) return "";
  const dayDiff=Math.round((_wallDayUTC(pk,tz)-_wallDayUTC(now,tz))/86400000);
  if(!Number.isFinite(dayDiff)) return "";
  if(dayDiff<=0) return tx("tax.aujourd-hui_79fc69",lang);
  if(dayDiff===1) return tx("tax.demain_83d802",lang);
  return `J−${dayDiff}`;
};
// Compte à rebours avant retrait (<24h) : « dans 22 h 40 min » — vide sinon.
const pickupCountdownLabel=(pickupAt,lang=ACTIVE_LANG,now=Date.now(),tz=LUX_TZ)=>{
  const pk=pickupToEpoch(pickupAt,tz);
  if(!Number.isFinite(pk)) return "";
  const diff=pk-now;
  if(diff<=0||diff>=24*3600000) return "";
  const h=Math.floor(diff/3600000),m=Math.floor((diff%3600000)/60000);
  const core=h>0?`${h} h ${String(m).padStart(2,"0")} min`:`${Math.max(1,m)} min`;
  return langPick(lang,`dans ${core}`,`in ${core}`,`in ${core}`,`em ${core}`);
};
// Chips rapides du formulaire : pré-remplissent le datetime-local (« Ce soir 18:00 » masqué si trop tard).
const quickPickupChips=(lang=ACTIVE_LANG,now=new Date())=>{
  const mk=(d,h)=>`${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}T${pad2(h)}:00`;
  const chips=[];
  if(now.getHours()<16){
    chips.push({label:`${tx("tax.ce-soir_c5d900",lang)} 18:00`,value:mk(now,18)});
  }
  //  Audit 23/09/2026 (lot client 2, constat #9) : « demain », c'est la date CIVILE suivante — pas maintenant + 24 h, qui, la
  //   nuit du changement d'heure, retombait sur aujourd'hui (doublon de « Ce soir 18:00 », même clé React) ou sautait un jour.
  const tomorrow=new Date(now.getFullYear(),now.getMonth(),now.getDate()+1,12);
  chips.push({label:`${tx("tax.demain_83d802",lang)} 18:00`,value:mk(tomorrow,18)});
  const sat=new Date(now.getTime());
  do{sat.setDate(sat.getDate()+1);}while(sat.getDay()!==6);
  chips.push({label:`${tx("tax.samedi_acfc23",lang)} 10:00`,value:mk(sat,10)});
  return chips;
};
// ─── Boost Price — règles de durée & monétisation ────────────────────────────
// INTERRUPTEUR MAÎTRE de la monétisation. false = tout reste GRATUIT aujourd'hui (toutes les durées
// jusqu'à 7j accessibles à tous ; seul le custom est plafonné à maxNonPremiumDays). Le jour de
// l'activation payante : passer monetizationLive à true + brancher le paiement sur profiles.boost_tier
// (free/plus/premium). Aucune autre modification de code nécessaire.
const BOOST_RULES={
  monetizationLive:false,
  maxNonPremiumDays:7,        // plafond dur de durée (jours) pour les tiers non-premium
  // tier minimum requis par option de durée, appliqué UNIQUEMENT quand monetizationLive=true
  durations:{today:"free","24h":"free","3d":"plus","7d":"plus",custom:"premium"},
};
const BOOST_TIER_RANK={free:0,plus:1,premium:2};
const boostTierOf=user=>BOOST_TIER_RANK[user?.boostTier]!=null?user.boostTier:"free";
// Option de durée autorisée pour ce tier ? (tant que monetizationLive=false → tout est autorisé)
const boostDurationAllowed=(kind,tier="free")=>{
  if(!BOOST_RULES.monetizationLive) return true;
  const need=BOOST_RULES.durations[kind]||"free";
  return (BOOST_TIER_RANK[tier]||0)>=(BOOST_TIER_RANK[need]||0);
};
// Durée max (jours) qu'un tier peut viser via custom. premium = illimité (Infinity).
const boostMaxDaysFor=(tier="free")=>tier==="premium"?Infinity:BOOST_RULES.maxNonPremiumDays;
const boostSaleEndFrom=(kind,custom,tier="free")=>{
  const now=new Date();
  if(kind==="today"){
    const end=new Date(now);
    end.setHours(23,59,59,999);
    return end.getTime();
  }
  if(kind==="3d") return Date.now()+3*86400000;
  if(kind==="7d") return Date.now()+7*86400000;
  if(kind==="custom"&&custom){
    const wanted=new Date(custom).getTime();
    if(!Number.isFinite(wanted)) return 0;
    const maxDays=boostMaxDaysFor(tier);
    return maxDays===Infinity?wanted:Math.min(wanted,Date.now()+maxDays*86400000);
  }
  return Date.now()+86400000;
};
const activeBoostSale=(item,now=Date.now())=>{
  const b=item?.boostSale;
  if(!b?.active||["sold","rented","completed"].includes(item?.status)) return null;
  const endsAt=Number(b.endsAt||0);
  const discount=Number(b.discountPrice||0);
  const original=Number(b.originalPrice||item?.price||0);
  if(!endsAt||endsAt<=now||!discount||discount>=original) return null;
  return {...b,endsAt,discountPrice:discount,originalPrice:original};
};
const listingPrice=(item,now=Date.now())=>activeBoostSale(item,now)?.discountPrice||Number(item?.price||0);
// ── RÈGLES PHOTO DU DÉPÔT (01/09/2026) ───────────────────────────────────────
//  Le minimum (2) et les plafonds (6 / 4) vivaient en TROIS endroits du formulaire :
//  le calcul de `photoLimit`, la validation `photos.length<2`, et le libellé « 2 min ·
//  4 max » — trois copies d'un même chiffre, donc trois occasions de diverger. Ici,
//  une seule source ; le libellé et la validation en DÉRIVENT.
//  Elles vivent dans 03 (règles de domaine) et non dans le composant, pour être
//  exerçables par un test node — cf. tests/publish-photo-intake.test.mjs.
const PHOTO_MIN=2;                       // plancher de publication (photos originales)
const PHOTO_MAX_RICH=6;                  // immo + voiture : le bien mérite plus d'angles
const PHOTO_MAX_PLAIN=4;                 // travaux + autres
const photoLimitFor=catId=>(catId==="immo"||catId==="voiture")?PHOTO_MAX_RICH:PHOTO_MAX_PLAIN;
// Répartit une sélection entre RETENUES et IGNORÉES pour cause de plafond.
//  Le point n'est PAS de couper — le formulaire coupait déjà. Le point est de rendre
//  les IGNORÉES, pour que l'appelant puisse le DIRE. Avant, choisir 10 photos quand il
//  restait 4 places en faisait disparaître 6 sans un mot ; « pas de plafond silencieux »
//  est une règle de la maison, elle vaut aussi pour l'utilisateur.
//  N'a AUCUN avis sur le format ni le poids : ces deux refus-là parlent déjà (bandeau
//  d'erreur) et restent strictement inchangés.
const photoIntake=(dejaLa,entrantes,limite)=>{
  const libres=Math.max(0,Number(limite||0)-Number(dejaLa||0));
  const liste=Array.isArray(entrantes)?entrantes:[];
  return {retenues:liste.slice(0,libres),ignorees:liste.slice(libres)};
};
// ── ORDRE DES PHOTOS (chantier 3 post-Lot A, 01/09/2026) ─────────────────────
//  La COUVERTURE EST l'index 0 — c'est déjà le contrat de toute la chaîne
//  (submit : urls/photoMeta/photoFiles/img dérivent TOUS de l'ordre du tableau ;
//  la passerelle R2 consomme par index ; le brouillon stocke le tableau ordonné).
//  Réordonner l'état réordonne donc TOUT, sans un octet d'egress ni une ligne de
//  backend. Ces deux fonctions PURES sont la seule autorité de réordonnancement :
//  le composant (boutons, clavier, drag) ne fait que les appeler.
//  · movePhoto : DÉPLACEMENT (pas un échange) — l'ordre relatif des autres est
//    STABLE ; bornes clampées ; from===to rend LA MÊME référence (pas de re-render).
//  · makeCover : la photo i passe en tête, l'ordre relatif du reste est PRÉSERVÉ
//    (un swap détruirait un ordre que l'utilisateur a soigné).
const movePhoto=(photos,from,to)=>{
  const arr=Array.isArray(photos)?photos:[];
  const f=Math.max(0,Math.min(arr.length-1,Math.trunc(Number(from))));
  const t=Math.max(0,Math.min(arr.length-1,Math.trunc(Number(to))));
  if(!arr.length||!Number.isFinite(f)||!Number.isFinite(t)||f===t) return arr;
  const next=arr.slice();
  const [ph]=next.splice(f,1);
  next.splice(t,0,ph);
  return next;
};
const makeCover=(photos,i)=>movePhoto(photos,i,0);
// ── REPÈRE DE PRIX VENDEUR — couche PURE (chantier 5b, 02/09/2026) ───────────
//  Le serveur (RPC `listing_price_reference`, bornée O(1000), plancher k=5) rend
//  {n,p25,p50,p75,tier} ou null. Ici, la partie PURE et bancable :
//  · priceRefRequest(form, editId) → {key, params} ou null. `key` = tuple STABLE des
//    champs DÉTERMINANTS — le cache et le débounce s'y accrochent. La frappe du PRIX
//    ne change PAS la clé : se positionner ne coûte JAMAIS un octet de réseau.
//  · priceRefPosition(prix, ref) → "below"|"within"|"above"|null (bandes P25/P75).
//    Formulation NEUTRE en aval : un repère informe, il ne juge jamais (règle maison :
//    aucune pénalité sur un geste bénin — ici pas même une couleur d'erreur).
const priceRefRequest=(form,editId)=>{
  if(!form||!form.catId) return null;
  const adType=form.catId==="travaux"?"service":(form.adType||"sale");
  const p={p_cat:form.catId,p_ad_type:adType};
  if(form.catId==="voiture"){
    const brand=form.brand==="Autre"?String(form.brandOther||"").trim():String(form.brand||"").trim();
    const model=(form.model==="Autre"||form.brand==="Autre")?String(form.modelOther||"").trim():String(form.model||"").trim();
    const year=Number(form.year);
    if(brand) p.p_brand=brand;
    if(model) p.p_model=model;
    if(Number.isFinite(year)&&year>1900) p.p_year=Math.trunc(year);
  }else if(form.catId==="immo"){
    if(form.propType) p.p_prop_type=form.propType;
    if(form.city) p.p_city=String(form.city).trim();
  }else if(form.catId==="travaux"){
    if(form.workType) p.p_work_type=form.workType;
  }else{
    if(form.otherCat) p.p_other_cat=form.otherCat;
    if(form.otherSubCat) p.p_other_sub=form.otherSubCat;
  }
  const eid=Number(editId);
  if(Number.isFinite(eid)&&eid>0) p.p_exclude_id=eid;
  return {key:JSON.stringify(p),params:p};
};
const priceRefPosition=(prix,ref)=>{
  const p=Number(prix);
  if(!ref||!Number.isFinite(p)||p<=0) return null;
  const p25=Number(ref.p25),p75=Number(ref.p75);
  if(!Number.isFinite(p25)||!Number.isFinite(p75)) return null;
  if(p<p25) return "below";
  if(p>p75) return "above";
  return "within";
};
// ── BROUILLON DU DÉPÔT — couche PURE (chantier 2 post-Lot A, 01/09/2026) ─────
//  Le contrat : RIEN ne se perd, JAMAIS, sans qu'on demande rien. Fermer sauve en
//  silence (IndexedDB — les Blobs photo/vidéo y entrent SANS sérialisation),
//  rouvrir reprend automatiquement. Le dialogue « Abandonner ? » de la vague 2
//  meurt en création : le brouillon rend la fermeture RÉVERSIBLE, et on ne
//  confirme pas le réversible. (Il survit en ÉDITION — pas de brouillon là-bas :
//  un brouillon d'édition divergerait de la source de vérité serveur — et en
//  REPLI quand IndexedDB est indisponible.)
//  Ici : la partie PURE et bancable — (form ⇄ record) + péremption. Un instrument
//  qui accumule porte sa propre péremption : TTL 14 j, et un schéma inconnu est
//  ÉCARTÉ, jamais interprété.
const DRAFT_SCHEMA_V=1;
const DRAFT_TTL_MS=14*24*3600*1000;
//  form → record STOCKABLE. Les objectURLs (morts à la fermeture de l'onglet) ne
//  sont JAMAIS stockés : une photo voyage par son Blob (_blob, vague 3) ou, repli
//  de compression, par sa data URL ; tout le reste est écarté. La vidéo voyage par
//  son _file ; son thumb n'est gardé que s'il est un data URL autonome.
const draftRecordFromForm=(form,now=Date.now())=>{
  const {photos,video,...rest}=form||{};
  return {
    v:DRAFT_SCHEMA_V,savedAt:now,
    form:rest,
    photos:(photos||[]).map(p=>({
      name:p.name||"photo",size:p.size||0,type:p.type||"",lastModified:p.lastModified||0,
      blob:(p._blob&&typeof p._blob.arrayBuffer==="function")?p._blob:null,
      dataUrl:(!p._blob&&typeof p.url==="string"&&p.url.startsWith("data:"))?p.url:null,
    })).filter(p=>p.blob||p.dataUrl),
    video:(video&&video._file&&typeof video._file.arrayBuffer==="function")?{
      name:video.name||"video.mp4",meta:video.meta||null,blob:video._file,
      thumb:(typeof video.thumb==="string"&&video.thumb.startsWith("data:"))?video.thumb:null,
    }:null,
  };
};
//  record → form AFFICHABLE (objectURLs recréés depuis les Blobs). null si le
//  record est d'un autre schéma ou illisible — l'appelant repart alors de blank.
const formFromDraftRecord=(record,blank)=>{
  if(!record||record.v!==DRAFT_SCHEMA_V||!record.form||typeof record.form!=="object") return null;
  try{
    const photos=(record.photos||[]).map(p=>{
      if(p.blob) return {name:p.name,size:p.size,type:p.type,lastModified:p.lastModified,url:URL.createObjectURL(p.blob),_blob:p.blob};
      if(p.dataUrl) return {name:p.name,size:p.size,type:p.type,lastModified:p.lastModified,url:p.dataUrl};
      return null;
    }).filter(Boolean);
    const video=record.video&&record.video.blob
      ?{name:record.video.name,url:URL.createObjectURL(record.video.blob),thumb:record.video.thumb||null,_file:record.video.blob,meta:record.video.meta||null}
      :null;
    return {...blank,...record.form,photos,video};
  }catch(e){return null;}
};
const draftIsStale=(record,now=Date.now())=>!record||!Number.isFinite(record.savedAt)||(now-record.savedAt)>DRAFT_TTL_MS;
// ── VALIDATION DU DÉPÔT (Lot A · vague 2, 01/09/2026) ────────────────────────
//  AVANT : six `return` en cascade dans submit() — l'utilisateur découvrait UNE
//  erreur par soumission, et le bandeau naissait en HAUT d'un panneau 100dvh dont
//  il avait déjà scrollé le bas : il tapait « Publier », rien ne bougeait à l'écran.
//  ICI : la MÊME sémantique de refus (mêmes champs, mêmes seuils, à l'identique),
//  mais TOUT D'UN COUP, en fonction PURE — donc testable en millisecondes
//  (tests/publish-validate.test.mjs) et affichable champ par champ.
//  Elle rend des CODES, pas des messages : l'i18n reste dans le composant (les
//  tuples L() y sont déjà, et le codemod taxonomie ne doit pas voir de nouveaux
//  tuples langPick littéraux ici).
//  L'ordre du tableau = l'ordre du DOM du formulaire (titre → ville → prix →
//  champs métier → description → photos) : le résumé d'erreurs se lit comme la page.
//  `now` est injectable UNIQUEMENT pour la testabilité de la borne d'année.
//  TITLE_MAX / DESC_MAX : les plafonds du serveur (migration `un_texte_a_une_fin`, 18/09/2026),
//  recopies ici a l'identique. Si l'un bouge la-bas, il bouge ici : `tests/un-texte-a-une-fin.test.mjs`
//  compare les deux et rougit sinon.
const TITLE_MAX=300, DESC_MAX=4000;
//  ⭐ LES PLAFONDS NUMERIQUES DU SERVEUR (migration `un_nombre_a_deux_bords`, 19/09/2026), recopies ici a
//  l'identique. Meme doctrine que TITLE_MAX/DESC_MAX et que `powerHp` : le dire a l'ecran, avec une phrase ET
//  un chiffre, plutot que de laisser partir un 23514 opaque que personne ne sait lire. Le serveur reste la
//  seule regle qui ne se contourne pas ; ceci n'est que la politesse de l'ecran.
//  `tests/un-nombre-a-deux-bords.test.mjs` compare ces valeurs a celles de la migration et rougit si l'une
//  bouge sans l'autre.
//  ⚠️ L'ETAGE est le seul borne DES DEUX COTES : il n'avait AUCUNE borne, et un sous-sol se dit en negatif.
const NUM_MAX={price:1000000000,rentPrice:1000000000,charges:10000000,deposit:10000000,area:1000000,rooms:100,bathrooms:50,wc:50,garage:50,mileage:6000000};
const FLOOR_MIN=-10, FLOOR_MAX=300;
//  `photoMin` : PHOTO_MIN pour une annonce neuve ; en ÉDITION, l'appelant passe min(PHOTO_MIN, photos déjà en place) — miroir de
//  la garde serveur du 10/09 (elle ne juge que ce qui APPAUVRIT : une annonce ancienne à 0 ou 1 photo se modifie et se booste
//  sans qu'on lui réclame ce qu'elle n'a jamais eu). Bug vu le 01/10 : « Activer Boost Sale » sur une annonce d'avant la règle
//  → « Ajoutez au moins 2 photos ».
const validatePublish=(form,{now=Date.now(),photoMin=PHOTO_MIN}={})=>{
  const errs=[];
  const price=form.adType==="rent"?Number(form.rentPrice):Number(form.price);
  const titre=String(form.title||"").trim();
  if(!titre) errs.push({champ:"title",code:"title"});
  // Miroir EXACT du CHECK serveur listings_title_len (<= 300). Meme doctrine que powerHp :
  // le dire ici, avec un message, plutot que de laisser partir un 23514 opaque. Le serveur
  // reste la seule regle qui ne se contourne pas ; ceci n'est que la politesse de l'ecran.
  else if(titre.length>TITLE_MAX) errs.push({champ:"title",code:"titleLong"});
  if(!String(form.city||"").trim()) errs.push({champ:"city",code:"city"});
  if(!price||price<=0) errs.push({champ:form.adType==="rent"?"rentPrice":"price",code:"price"});
  if(form.catId==="immo"&&Number(form.area||0)<=0) errs.push({champ:"area",code:"area"});
  if(form.catId==="voiture"){
    const y=Number(form.year);
    if(!form.year||y<1950||y>new Date(now).getFullYear()+1) errs.push({champ:"year",code:"year"});
    if(form.mileage===""||form.mileage==null||Number(form.mileage)<0) errs.push({champ:"mileage",code:"mileage"});
    // Puissance OPTIONNELLE — mais si elle est renseignee, elle doit etre plausible :
    // miroir EXACT du CHECK serveur listings_power_hp_bounds (1..2000). Refuser ici,
    // avec un message, plutot que de laisser l'insert echouer en 23514 opaque.
    const hp=String(form.powerHp??"").trim();
    if(hp!==""&&(!Number.isFinite(Number(hp))||Number(hp)<1||Number(hp)>2000)) errs.push({champ:"powerHp",code:"powerHp"});
  }
  //  Les plafonds ne s'imposent qu'a un champ RENSEIGNE : un champ vide reste vide, et une valeur illisible
  //  est deja refusee ailleurs. L'ordre de la table suit celui du DOM, comme le reste du tableau d'erreurs.
  for(const champ of Object.keys(NUM_MAX)){
    const v=String(form[champ]??"").trim();
    if(v!==""&&Number.isFinite(Number(v))&&Number(v)>NUM_MAX[champ]) errs.push({champ,code:champ+"Max"});
  }
  const etage=String(form.floor??"").trim();
  if(etage!==""&&Number.isFinite(Number(etage))&&(Number(etage)<FLOOR_MIN||Number(etage)>FLOOR_MAX)) errs.push({champ:"floor",code:"floorRange"});
  const desc=String(form.desc||"").trim();
  if(!desc) errs.push({champ:"desc",code:"desc"});
  else if(desc.length<20) errs.push({champ:"desc",code:"descShort"});
  // Miroir EXACT du CHECK serveur listings_description_len (<= 4000 depuis C7 · T21 : 20 000 était un plafond
  // qu'aucune annonce n'approchait — la plus longue description en production fait 194 — et un texte stocké se
  // relit, donc se paie, à CHAQUE lecture par CHAQUE visiteur. 4 000 est ce qu'autorisent les sites comparables).
  else if(desc.length>DESC_MAX) errs.push({champ:"desc",code:"descLong"});
  if((form.photos||[]).length<photoMin) errs.push({champ:"photos",code:"photos"});
  return errs;
};
// ── V4-T1 (17/07/2026) — « cette annonce correspond-elle à cette alerte / recherche ? »
//  RÈGLES MÉTIER PURES, remontées de App() : elles ne touchaient AUCUN état et AUCUN
//  hook (mesuré par app-closure : fermeture 0, 0 hook) — elles n'avaient donc rien à
//  faire dans un composant. Ici, elles sont testables en millisecondes.
//  ⚠️ `tests/test_alert_match.mjs` testait jusqu'ici une RÉPLIQUE de cette logique
//  (« Réplique fidèle de la logique LIEU des matchers ») — une copie dérive toujours.
//  `tests/domain-match.test.mjs` teste désormais CE code, le vrai.
//  Elles vivent dans 03 (et non 01) parce qu'elles ont besoin de `listingPrice`
//  ci-dessus ; 03 importe DÉJÀ de 01 ⇒ zéro nouvelle arête de dépendance.
//  (04/10, A17) LA MÊME RÈGLE QUE LE SERVEUR. Depuis A17 c'est la BASE qui prévient les alertes (alerte_correspond, prouvée égale
//   à listings_feed sur le catalogue) ; ce miroir sert l'écran (la carte « Votre alerte » du fil vivant) et ne doit pas dire
//   autre chose. Avant : sous-chaîne SENSIBLE aux accents (« velo » ne trouvait pas « vélo »), champs différents de ceux du
//   marché, sous-filtres ignorés. Désormais : sans accents (comme immutable_unaccent : diacritiques ôtés, œ/æ/ß développés),
//   les 11 champs du marché, le libellé de la catégorie (français + langue du membre), et les six sous-filtres.
//   ⚠️ Un objet MAIGRE (l'échantillon d'accueil n'a pas la description) peut sous-compter : le serveur, lui, a tout.
const sansAccents=s=>String(s??"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/œ/g,"oe").replace(/æ/g,"ae").replace(/ß/g,"ss");
const itemMatchesAlert=(item,a,lang="fr")=>{
  if(a.cat&&item.catId!==a.cat) return false;
  if(a.adType&&item.adType!==a.adType) return false;
  if(a.other&&item.otherCat!==a.other) return false;
  if(a.otherSub&&(item.otherSubCat||"")!==a.otherSub) return false;
  if(a.propType&&item.catId==="immo"&&item.propType!==a.propType) return false;
  if(a.rooms!=null&&a.rooms!==""&&item.catId==="immo"&&Number(item.rooms||0)<Number(a.rooms)) return false;
  if(a.brand&&item.catId==="voiture"&&sansAccents(item.brand)!==sansAccents(a.brand)) return false;
  if(a.fuel&&item.catId==="voiture"&&sansAccents(item.fuel)!==sansAccents(a.fuel)) return false;
  if(a.workType&&item.catId==="travaux"&&sansAccents(item.workType)!==sansAccents(a.workType)) return false;
  const _aOrigin=(a.coords&&Number.isFinite(a.coords.lat)&&Number.isFinite(a.coords.lng))?a.coords:findCity(a.country,a.city);
  const _aRad=!!_aOrigin&&Number(a.radius||0)>0;
  if(!_aRad&&a.country&&a.country!=="all"&&countryOfItem(item)!==a.country) return false; // Option A : lieu+rayon → rayon seul (frontières confondues) ; sinon filtre pays (aligné feed)
  if(a.city&&a.city!=="all"){
    if(_aRad){
      if(distanceKm(_aOrigin,itemPoint(item))>Number(a.radius)) return false;
    }else if((item.city||"").toLowerCase()!==String(a.city).toLowerCase()) return false;
  }
  if(a.max&&a.max<5000000&&listingPrice(item)>a.max) return false;
  if(a.query){
    const q=sansAccents(a.query);
    const hay=sansAccents([item.title,item.city,item.desc??item.description,item.fuel,item.brand,item.model,item.propType,item.condition,item.otherCat,item.workType,item.otherSubCat].map(x=>x??"").join(" "));
    const lbl=OTHER_MAP[item.otherCat]?.label;
    const parLibelle=!!lbl&&(sansAccents(lbl.fr).includes(q)||sansAccents(lbl[lang]||lbl.fr).includes(q));
    if(!hay.includes(q)&&!parLibelle) return false;
  }
  return true;
};
//  (04/10, A17) UNE ALERTE EST-ELLE CELLE DE CETTE RECHERCHE ? Par ses CRITÈRES, pas par son nom. Vu par l'e2e S132 : une
//   recherche tapée passe par inferSearchCategory, qui ne déduit une catégorie QUE s'il y a des résultats — une alerte créée
//   sur « vélo » sans résultat s'appelle « "vélo" », la même frappe le lendemain (trois vélos publiés) donne « Autres ·
//   "vélo" » : la pilule ne la reconnaissait plus et proposait d'en créer une SECONDE (deux notifications par vélo).
//   LA RÈGLE : même texte (sans accents ni casse), mêmes filtres choisis ; la catégorie seule peut manquer d'UN côté, et
//   seulement si le texte n'est pas vide (c'est alors lui qui porte la recherche). Un filtre choisi à la main reste strict.
const memeAlerte=(a,c)=>{
  if(!a||!c) return false;
  const t=v=>sansAccents(v).replace(/\s+/g," ").trim();
  const vide=v=>v==null||v===""||v==="all";
  const egal=(x,y)=>(vide(x)&&vide(y))||(!vide(x)&&!vide(y)&&t(x)===t(y));
  const pieces=v=>(v==null||v===""||!Number(v))?0:Number(v);
  const plafond=v=>(v==null||v===""||!(Number(v)>0)||Number(v)>=5000000)?0:Number(v);
  const q=t(a.query);
  if(q!==t(c.query)) return false;
  if(!egal(a.cat,c.cat)&&!(q&&(vide(a.cat)||vide(c.cat)))) return false;
  for(const k of ["adType","other","otherSub","propType","brand","fuel","workType","country","city"]) if(!egal(a[k],c[k])) return false;
  if(!vide(a.city)&&Number(a.radius||0)!==Number(c.radius||0)) return false;
  return pieces(a.rooms)===pieces(c.rooms)&&plafond(a.max)===plafond(c.max);
};
const itemMatchesSearchMemory=(item,s)=>{
  if(!s) return false;
  if(s.cat&&item.catId!==s.cat) return false;
  if(s.adType&&item.adType!==s.adType) return false;
  if(s.other&&item.otherCat!==s.other) return false;
  const _sOrigin=(s.coords&&Number.isFinite(s.coords.lat)&&Number.isFinite(s.coords.lng))?s.coords:findCity(s.country,s.city);
  const _sRad=!!_sOrigin&&Number(s.radius||0)>0;
  if(!_sRad&&s.country&&s.country!=="all"&&countryOfItem(item)!==s.country) return false; // Option A (aligné feed)
  if(s.city&&s.city!=="all"){
    if(_sRad){
      if(distanceKm(_sOrigin,itemPoint(item))>Number(s.radius||0)) return false;
    }else if((item.city||"").toLowerCase()!==String(s.city).toLowerCase()) return false;
  }
  if(s.max&&Number(s.max)<5000000&&listingPrice(item)>Number(s.max)) return false;
  const query=(s.query||s.label||"").trim();
  if(query){
    const hay=norm([item.title,item.city,item.address,item.desc,item.description,item.fuel,item.brand,item.model,item.propType,item.carType,item.workType,item.condition,item.gearbox,OTHER_MAP[item.otherCat]?.label?.fr].filter(Boolean).join(" "));
    const words=norm(query).split(/[^a-z0-9]+/).filter(w=>w.length>2);
    if(words.length&&words.filter(w=>hay.includes(w)).length<Math.min(2,words.length)) return false;
  }
  return true;
};
const boostSaleLabel=(endsAt,now=Date.now(),lang="fr")=>{
  const diff=Number(endsAt||0)-now;
  if(diff<=0) return "Terminé";
  const end=new Date(endsAt),today=new Date(now);
  const sameDay=end.toDateString()===today.toDateString();
  const words={
    fr:{tonight:"Se termine ce soir",in:"Se termine dans",days:"jours"},
    en:{tonight:"Ends tonight",in:"Ends in",days:"days"},
    de:{tonight:"Endet heute Abend",in:"Endet in",days:"Tagen"},
    pt:{tonight:"Termina hoje à noite",in:"Termina em",days:"dias"}
  }[lang]||{};
  if(sameDay) return words.tonight||"Se termine ce soir";
  const days=Math.ceil(diff/86400000);
  if(days<=1){
    const h=Math.floor(diff/3600000),m=Math.floor((diff%3600000)/60000);
    return `${words.in||"Se termine dans"} ${h>0?`${h}h ${m}min`:`${Math.max(1,m)}min`}`;
  }
  return `${words.in||"Se termine dans"} ${days} ${words.days||"jours"}`;
};
//  (05/10/2026) LA REMISE D'UNE OFFRE, telle que la carte la dit : un pourcentage ENTIER, arrondi au plus proche ; rien sous 1 %
//  (« −0 % » ne se dit pas). `boost` = ce que rend activeBoostSale (offre en cours, prix remisé < prix d'origine).
const remiseDuBoost=boost=>{
  const o=Number(boost&&boost.originalPrice), p=Number(boost&&boost.discountPrice);
  if(!(o>0)||!(p>0)||!(p<o)) return 0;
  const pct=Math.round((1-p/o)*100);
  return pct>=1?pct:0;
};
//  « −15 % » dans la langue du membre : le signe moins typographique, et l'espace que sa langue met (ou non) avant « % ».
const remiseDite=(pct,lang="fr")=>{
  if(!(pct>=1)) return "";
  let s;
  try{ s=new Intl.NumberFormat(localeForLang(lang),{style:"percent",maximumFractionDigits:0}).format(pct/100); }catch(e){ s=`${pct} %`; }
  return "\u2212"+s;
};
//  La fin de l'offre, EN COURT — à côté de la pastille de remise, sur une carte de téléphone, « Se termine dans 2 jours »
//  (boostSaleLabel) déborde : « jusqu'à ce soir » le jour même, « jusqu'à demain » le lendemain, sinon « encore N j ». "" si
//  l'offre est finie. Les jours sont ceux du CALENDRIER de l'appareil : une offre qui finit dans 6 jours et 5 heures dit « 6 j ».
const finCourteDuBoost=(endsAt,now=Date.now(),lang="fr")=>{
  const fin=Number(endsAt||0), diff=fin-now;
  if(!(diff>0)) return "";
  const jourDe=t=>{const d=new Date(t);return new Date(d.getFullYear(),d.getMonth(),d.getDate()).getTime();};
  const ecart=Math.round((jourDe(fin)-jourDe(now))/86400000);
  if(ecart<=0) return tx("tax.jusqu-a-ce-soir_24162b",lang);
  if(ecart===1) return tx("tax.jusqu-a-demain_df86eb",lang);
  return tx("tax.encore-n-j_257524",lang).replace("{n}",String(ecart));
};
//  (29/10/2026, lot PULSE — décision de Mounir) LA LIGNE DE LA FICHE d'une annonce en offre, téléphone et ordinateur : le PROPRIÉTAIRE
//   lit le nom de son produit et la fin (« Pulse · jusqu'à demain ») ; tout autre lecteur lit les mots des cartes et du plein écran
//   (« Sponsorisé · −15 % · jusqu'à demain ») — aucun nom de produit pour l'acheteur. La MÊME fin pour les deux (les jours du calendrier,
//   celle des cartes) : vu à l'écran, « Se termine dans 2 jours » (une durée arrondie) chez le vendeur contre « jusqu'à demain » chez
//   l'acheteur pour la même offre. Avant : « Boost Sale · … » pour tous. "" sans offre.
const ligneDeMiseEnAvant=(boost,proprietaire,now=Date.now(),lang="fr")=>{
  if(!boost) return "";
  if(proprietaire) return ["Pulse",finCourteDuBoost(boost.endsAt,now,lang)].filter(Boolean).join(" · ");
  return [tx("tax.sponsorise_845fbe",lang),remiseDite(remiseDuBoost(boost),lang),finCourteDuBoost(boost.endsAt,now,lang)].filter(Boolean).join(" · ");
};
const toDateTimeLocal=ts=>{
  if(!ts) return "";
  const d=new Date(Number(ts));
  if(Number.isNaN(d.getTime())) return "";
  d.setMinutes(d.getMinutes()-d.getTimezoneOffset());
  return d.toISOString().slice(0,16);
};
const stopBoostSalePatch=item=>({boostSale:{...(item?.boostSale||{}),active:false,stoppedAt:Date.now()}});
function useMinuteNow(interval=60000){
  const [now,setNow]=useState(Date.now());
  useEffect(()=>{const tid=setInterval(()=>setNow(Date.now()),interval);return()=>clearInterval(tid);},[interval]);
  return now;
}
//  (lot D · D6-j, 31/10/2026) LA LECTURE SEULE — un fil où le lecteur n'est PAS participant : la base y refuse toute écriture (D6-i),
//   l'écran n'y offre donc aucun geste. Seul l'ADMIN y entre (un membre ne charge que ses fils : canSeeConvo) — le rôle est EXIGÉ, si bien
//   qu'un membre ne peut JAMAIS être mis en lecture seule par construction (revue AVANT B2). Des participants absents ou vides = INCONNU,
//   jamais « hors du fil » (convoFromDB rend [] pour une colonne absente : le fil « Contact administration » ne doit pas se fermer).
const horsDuFil=(convo,me)=>!!convo&&!!me&&!!me.id&&me.role==="Admin"&&Array.isArray(convo.participants)&&convo.participants.length>0&&!convo.participants.includes(me.id);


export { horsDuFil, remiseDuBoost, remiseDite, finCourteDuBoost, ligneDeMiseEnAvant, NUM_MAX, FLOOR_MIN, FLOOR_MAX, BOOST_RULES, BOOST_TIER_RANK, PHOTO_MAX_PLAIN, PHOTO_MAX_RICH, PHOTO_MIN, DRAFT_SCHEMA_V, DRAFT_TTL_MS, draftIsStale, draftRecordFromForm, formFromDraftRecord, makeCover, movePhoto, photoIntake, photoLimitFor, priceRefPosition, priceRefRequest, validatePublish, activeBoostSale, boostDurationAllowed, boostMaxDaysFor, boostSaleEndFrom, boostSaleLabel, itemMatchesAlert, itemMatchesSearchMemory, memeAlerte, boostTierOf, fmtPickupHero, listingPrice, pickupCountdownLabel, pickupDayChip, quickPickupChips, offerCycleOf, offerFooterPhase, offerFooterView, reservationLiveState, stopBoostSalePatch, toDateTimeLocal, useMinuteNow, RESERVE_CHAIN_STATUSES, reservationChainOf, reservationChainState, visitPairState, exchangeStateOf };
